import { Plus, RefreshCcw, ShoppingBag } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Section } from '@/components/common/Blocks'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { useOrders, useRenewals } from '../../api'
import type { CrmDialog } from '../CrmDialogs'
import { OrderCard } from '../OrderCard'
import { RenewalList } from '../SalesLists'
import { useCrmDeletes } from '../useCrmDeletes'

type Props = { customerId: string; canWrite: boolean; canApprove: boolean; open: (d: CrmDialog) => void }

/** Müşterinin siparişleri, lisans kapsamı ve yaklaşan yenilemeleri. */
export function OrdersTab({ customerId, canWrite, canApprove, open }: Props) {
  const { t } = useTranslation()
  const orders = useOrders(customerId).data ?? []
  const renewals = (useRenewals().data ?? []).filter((r) => r.customerId === customerId)
  const deletes = useCrmDeletes()

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="mr-auto flex items-center gap-2 font-semibold">
          <ShoppingBag className="size-4 text-muted-foreground" aria-hidden />
          {t('crm.order.title')}
          <span className="text-sm font-normal text-muted-foreground">({orders.length})</span>
        </h2>
        <p className="w-full text-xs text-muted-foreground sm:w-auto">{t('crm.order.tabHint')}</p>
      </div>
      {orders.length === 0 ? (
        <div className="rounded-xl border bg-card shadow-card">
          <EmptyState icon={ShoppingBag} title={t('crm.order.empty')} description={t('crm.order.emptyHint')} />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              canWrite={canWrite}
              canApprove={canApprove}
              onApprove={() => open({ kind: 'approveOrder', order: o })}
              onReject={() => open({ kind: 'rejectOrder', order: o })}
              onDelete={() => void deletes.order(o)}
            />
          ))}
        </div>
      )}

      <Section
        title={t('crm.renewal.title')}
        icon={RefreshCcw}
        bodyClassName="p-0"
        actions={
          canWrite ? (
            <Button size="sm" onClick={() => open({ kind: 'renewal', renewal: null })}>
              <Plus />
              {t('crm.renewal.add')}
            </Button>
          ) : null
        }
      >
        <RenewalList rows={renewals} canWrite={canWrite} onEdit={(r) => open({ kind: 'renewal', renewal: r })} onDelete={(r) => void deletes.renewal(r)} />
      </Section>
    </div>
  )
}
